import {
  Business as EntityIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Link as UsagesIcon,
  ChevronRight as OpenIcon,
  FolderOpen as ProjectIcon,
  UploadFile as UploadIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Layout/Sidebar";
import {
  ACCEPTED_DOCUMENT_EXTENSIONS,
  DOC_KIND_LABELS,
  DocKind,
  Document,
  DocumentLink,
  documentsApi,
  EXTRACTION_STATUS_LABELS,
  formatFileSize,
} from "../services/documentsApi";

// Central document management space (#1013): the org-wide library.
// Any member with the research feature can upload and view; deleting is
// restricted to organization admins (the API enforces it — we surface 403s).

const EXTRACTION_CHIP_COLOR: Record<
  string,
  "default" | "success" | "warning" | "error"
> = {
  pending: "default",
  done: "success",
  low_quality: "warning",
  unsupported_scanned: "warning",
  failed: "error",
};

/** Where a link sits, for the line under its name. */
const usageSubtitle = (link: DocumentLink): string => {
  if (link.target_type === "project") return "Project reference material";
  if (link.target_type === "track_entity") {
    const where = [link.project_name, link.track_name]
      .filter(Boolean)
      .join(" · ");
    return where ? `Candidate in ${where}` : "Candidate";
  }
  return link.kg_namespace
    ? `Organization library · ${link.kg_namespace}`
    : "Organization library";
};

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<DocKind | "">("");
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: "success" | "info" | "error";
  } | null>(null);

  const [editDoc, setEditDoc] = useState<Document | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editKind, setEditKind] = useState<DocKind>("reference");

  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [deleteUsages, setDeleteUsages] = useState<DocumentLink[] | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [usagesDoc, setUsagesDoc] = useState<Document | null>(null);
  const [usagesLinks, setUsagesLinks] = useState<DocumentLink[] | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await documentsApi.listDocuments(
        kindFilter ? { docKind: kindFilter } : undefined,
      );
      setDocuments(response.results);
    } catch {
      setSnackbar({ message: "Failed to load documents", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [kindFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visibleDocuments = documents.filter((doc) => {
    if (!search) return true;
    const needle = search.toLowerCase();
    return (
      doc.filename.toLowerCase().includes(needle) ||
      doc.title.toLowerCase().includes(needle) ||
      doc.description.toLowerCase().includes(needle)
    );
  });

  const handleUpload = useCallback(
    async (files: FileList) => {
      setUploading(true);
      try {
        for (const file of Array.from(files)) {
          try {
            const result = await documentsApi.uploadDocument({ file });
            setSnackbar(
              result.created
                ? { message: `Uploaded ${file.name}`, severity: "success" }
                : {
                    message: `${file.name} already exists in the library`,
                    severity: "info",
                  },
            );
          } catch (error: any) {
            const status = error?.response?.status;
            setSnackbar({
              message:
                status === 413
                  ? `${file.name} is too large`
                  : `Failed to upload ${file.name}`,
              severity: "error",
            });
          }
        }
      } finally {
        setUploading(false);
        refresh();
      }
    },
    [refresh],
  );

  const openEdit = (doc: Document) => {
    setEditDoc(doc);
    setEditTitle(doc.title);
    setEditDescription(doc.description);
    setEditKind(doc.doc_kind);
  };

  const saveEdit = async () => {
    if (!editDoc) return;
    try {
      await documentsApi.updateDocument(editDoc.id, {
        title: editTitle,
        description: editDescription,
        doc_kind: editKind,
      });
      setSnackbar({ message: "Document updated", severity: "success" });
    } catch {
      setSnackbar({ message: "Failed to update document", severity: "error" });
    }
    setEditDoc(null);
    refresh();
  };

  const openDelete = async (doc: Document) => {
    setDeleteDoc(doc);
    setDeleteUsages(null);
    try {
      const usages = await documentsApi.getUsages(doc.id);
      setDeleteUsages(usages.links);
    } catch {
      setDeleteUsages([]);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await documentsApi.deleteDocument(deleteDoc.id);
      setSnackbar({
        message: `Deleted ${deleteDoc.filename}`,
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        message:
          error?.response?.status === 403
            ? "Only organization admins can delete documents"
            : "Failed to delete document",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setDeleteDoc(null);
      refresh();
    }
  };

  const showUsages = async (doc: Document) => {
    setUsagesDoc(doc);
    setUsagesLinks(null);
    try {
      const usages = await documentsApi.getUsages(doc.id);
      setUsagesLinks(usages.links);
    } catch {
      setUsagesLinks([]);
      setSnackbar({ message: "Failed to load usages", severity: "error" });
    }
  };

  /** Open a link's target: its project, the track it sits in, and the entity. */
  const openUsage = (link: DocumentLink) => {
    if (!link.project_id) return;
    const params = new URLSearchParams();
    if (link.track_id) {
      // The research view only activates a track section when both track and
      // stage are present. The long list holds every decision state except
      // shortlisted, so landing a shortlisted candidate there would show a
      // list it is not in.
      params.set("track", link.track_id);
      params.set(
        "stage",
        link.entity_state === "shortlisted" ? "shortList" : "longList",
      );
    }
    if (link.entity_id) params.set("detail_entity_id", link.entity_id);
    const query = params.toString();
    setUsagesDoc(null);
    navigate(`/research/${link.project_id}${query ? `?${query}` : ""}`);
  };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flexGrow: 1, ml: "60px", overflow: "auto" }}
      >
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your organization's document library — reference materials and
              candidate documents used across research projects.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={ACCEPTED_DOCUMENT_EXTENSIONS}
            onChange={(event) => {
              if (event.target.files?.length) handleUpload(event.target.files);
              event.target.value = "";
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: 280 }}
          />
          <TextField
            size="small"
            select
            label="Kind"
            value={kindFilter}
            onChange={(event) =>
              setKindFilter(event.target.value as DocKind | "")
            }
            sx={{ width: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="reference">Reference</MenuItem>
            <MenuItem value="candidate_evidence">Candidate Evidence</MenuItem>
          </TextField>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          {/* Fixed layout: filenames run to 100+ characters, and with the
              default content-driven layout one of them widens the Document
              column until every other column is squeezed out of view. */}
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell sx={{ width: 150 }}>Kind</TableCell>
                <TableCell sx={{ width: 90 }}>Size</TableCell>
                <TableCell sx={{ width: 130 }}>Extraction</TableCell>
                <TableCell sx={{ width: 110 }}>Uploaded</TableCell>
                <TableCell align="right" sx={{ width: 170 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : visibleDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No documents yet — upload reference materials or attach
                      documents to candidates from a research project.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleDocuments.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Tooltip
                        title={
                          doc.title ? `${doc.title} — ${doc.filename}` : doc.filename
                        }
                        placement="top-start"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                            noWrap
                          >
                            {doc.title || doc.filename}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            display="block"
                          >
                            {doc.filename}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={DOC_KIND_LABELS[doc.doc_kind] || doc.doc_kind}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatFileSize(doc.size_bytes)}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          EXTRACTION_STATUS_LABELS[doc.extraction_status] ||
                          doc.extraction_status
                        }
                        size="small"
                        color={
                          EXTRACTION_CHIP_COLOR[doc.extraction_status] ||
                          "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(doc.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Where is this used?">
                        <IconButton size="small" onClick={() => showUsages(doc)}>
                          <UsagesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() =>
                            documentsApi.downloadDocument(doc.id, doc.filename)
                          }
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(doc)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete (org admins)">
                        <IconButton
                          size="small"
                          onClick={() => openDelete(doc)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Usages dialog */}
        <Dialog
          open={usagesDoc !== null}
          onClose={() => setUsagesDoc(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Linked to
            <Typography variant="body2" color="text.secondary" noWrap>
              {usagesDoc?.title || usagesDoc?.filename}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {usagesLinks === null ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : usagesLinks.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Not linked anywhere yet. Attach it to a project as reference
                  material, or to a candidate from a research project.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {usagesLinks.map((link) => {
                  const openable = Boolean(link.project_id);
                  return (
                    <ListItemButton
                      key={link.id}
                      onClick={() => openUsage(link)}
                      disabled={!openable}
                      divider
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {link.target_type === "project" ? (
                          <ProjectIcon fontSize="small" />
                        ) : (
                          <EntityIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={link.target_label || link.target}
                        primaryTypographyProps={{ noWrap: true }}
                        secondary={usageSubtitle(link)}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                      {openable && <OpenIcon fontSize="small" color="action" />}
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUsagesDoc(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit dialog */}
        <Dialog
          open={editDoc !== null}
          onClose={() => setEditDoc(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit document</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <TextField
              label="Title"
              value={editTitle}
              onChange={(event) => setEditTitle(event.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(event) => setEditDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Kind"
              value={editKind}
              onChange={(event) => setEditKind(event.target.value as DocKind)}
            >
              <MenuItem value="reference">Reference</MenuItem>
              <MenuItem value="candidate_evidence">Candidate Evidence</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDoc(null)}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirm dialog */}
        <Dialog
          open={deleteDoc !== null}
          onClose={() => setDeleteDoc(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete {deleteDoc?.filename}?</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <Typography variant="body2" sx={{ mb: 1 }}>
                This immediately deletes the document for the whole
                organization. Evidence already derived in completed evaluations
                is retained.
              </Typography>
              {deleteUsages === null ? (
                <CircularProgress size={18} />
              ) : deleteUsages.length > 0 ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Currently used in:
                  </Typography>
                  {deleteUsages.map((link) => (
                    <Typography key={link.id} variant="body2">
                      • {link.target}
                    </Typography>
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not linked anywhere.
                </Typography>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDoc(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar !== null}
          autoHideDuration={5000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snackbar?.severity || "info"}
            onClose={() => setSnackbar(null)}
            variant="filled"
          >
            {snackbar?.message}
          </Alert>
        </Snackbar>
      </Box>
      </Box>
    </Box>
  );
};

export default DocumentsPage;
